import type { HeroProps } from '@/components/home/Hero';
import type {
  CustomersReviewsProps,
  HowItWorksProps,
  InstantEverythingProps,
  ProductsProps,
  SwitchingProps,
  TrustProps,
} from '@/components/shared';
import { sharedConfig } from '@/pageConfig/shared';
import { carExtraDisclaimer } from '@/pageTemplate/CarPageTemplate';
import aRated from '@/static/assets/home/a-rated.png';
import bCorp from '@/static/assets/home/b-corp.png';
import switchingMobile from '@/static/assets/home/bg-switching-mobile.webp';
import switching from '@/static/assets/home/bg-switching.webp';
import nyse from '@/static/assets/home/nyse.png';
import pizzaDesktop from '@/static/assets/home/pizza_desktop_en-US.svg';
import pizzaMobile1 from '@/static/assets/home/pizza_mobile_1.svg';
import pizzaMobile2 from '@/static/assets/home/pizza_mobile_2.svg';
import pizzaMobile3 from '@/static/assets/home/pizza_mobile_3.svg';
import type { BasePageConfig } from '../../BasePageConfig';

const pizza = {
  desktop: pizzaDesktop,
  mobile: {
    1: pizzaMobile1,
    2: pizzaMobile2,
    3: pizzaMobile3,
  },
};

const productJsonld = {
  '@context': 'http://schema.org',
  '@type': 'Organization',
  name: 'Lemonade',
  url: 'https://www.lemonade.com',
  logo: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  sameAs: [
    'https://www.facebook.com/Lemonade/',
    'https://twitter.com/lemonade_inc',
    'https://www.instagram.com/lemonade_inc/',
    'https://plus.google.com/u/0/b/111995871012779456331/+Lemonade_Inc',
    'https://www.linkedin.com/company-beta/1201453/',
    'https://www.pinterest.com/lemonade_inc',
    'https://en.wikipedia.org/wiki/Lemonade,_Inc.',
    'https://www.wikidata.org/wiki/Q37019245',
    'https://www.youtube.com/c/Lemonade_Inc',
  ],
};

const agencyJsonld = {
  '@context': 'http://schema.org',
  '@type': 'InsuranceAgency',
  name: 'Lemonade',
  tickerSymbol: ['NYSE:LMND'],
  address: '5 Crosby St. 3rd floor, New York, NY 10013 USA',
  image: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  description:
    'Lemonade is a property and casualty insurance company powered by technology and driven by social good. Insuring in the United States, Lemonade offers urban dwellers home, renters and condo insurance policies in 90 seconds, starting from $5/month. No brokers, no paperwork.',
  url: 'https://www.lemonade.com',
  areaServed: 'United States',
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  customersReviews: CustomersReviewsProps;
  products: ProductsProps;
  instantEverything: InstantEverythingProps;
  switching: SwitchingProps;
  howItWorks: HowItWorksProps;
  trust: TrustProps;
}

export const config: Config = {
  seo: { ...sharedConfig['en-US'].seo, jsonld: [agencyJsonld, productJsonld] },
  layout: {
    ...sharedConfig['en-US'].layout,
    extraFooterDisclaimer: carExtraDisclaimer(),
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['en-US'].layout.headerActions,
      CTAs: {
        ...sharedConfig['en-US'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['en-US'].layout.headerActions.CTAs.checkOurPrices,
          short: {
            singular: 'Check our price',
            plural: 'Check our prices',
          },
        },
      },
    },
  },
  hero: {
    title: 'Forget everything you\nknow about insurance',
    subtitle: 'Instant everything. Incredible prices. Big heart.',
    ctaLabel: 'Check our Prices',
    cart: {
      singular: 'Back to quote',
      plural: 'Back to quotes',
    },
  },
  customersReviews: {
    title: 'The (Almost) 5 Star Insurance Company',
    subtitle:
      'Lemonade has earned 4.9 stars in the App store, and\nis also top-rated by Supermoney, Clearsurance, and others',
    darkBackground: true,
  },
  products: {
    title: 'Incredible Prices. Monthly Subscription.<br/> Bundle Discounts.',
    subtitle: 'Amazing savings when you bundle',
    products: [
      {
        title: 'Renters',
        subtitle: 'Coverage for your stuff, in<br/> and out of your home',
        price: 'From $5/mo',
        product: 'home_us_renters',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'Renters Insurance',
      },
      {
        title: 'Homeowners',
        subtitle: 'Protection for your home and stuff',
        price: 'From $25/mo',
        product: 'home_us_owners',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'Homeowners Insurance',
      },
      {
        title: 'Car',
        subtitle: 'Protect your car, passengers,<br/>and the planet',
        price: 'as low as $30/mo',
        product: 'car',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'Car Insurance',
      },
      {
        title: 'Pet',
        subtitle: 'Health insurance for furry<br/>family members',
        price: 'From $10/mo',
        product: 'pet',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'Pet Health Insurance',
      },
      {
        title: 'Term Life',
        subtitle: 'Protecting the people<br/>you love',
        price: 'From $8/mo',
        product: 'life',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'Life Insurance',
      },
    ],
    trackingLabel: 'home_us',
  },
  instantEverything: {
    title: 'Instant everything',
    subtitle:
      'We use AI to craft the perfect policy for you, and to pay out claims. \n It couldn’t be easier or faster',
    appSketchContentLeft: 'As little as<br/><b>90 seconds</b>',
    appSketchContentRight: '<b>3 seconds</b><br/>to get paid',
    darkBackground: true,
    adjustments: {
      contentSectionLeft: {
        left: { lg: '-425px' },
      },
      contentSectionRight: {
        right: { lg: '-415px' },
      },
    },
  },
  switching: {
    title: 'Already insured? We’ll help you switch!',
    subtitle: 'People left these insurance companies to join Lemonade:',
    cta: {
      ctaLabel: 'Check prices and switch',
      ctaUrl: '/onboarding/start',
    },
    compDesktopImgUrl: switching,
    compMobileImgUrl: switchingMobile,
  },
  howItWorks: {
    title: 'How Lemonade Works',
    subtitle: `Lemonade reverses the traditional insurance model. We treat the premiums
    you pay as if it's your money, not ours. With Lemonade, everything
    becomes simple and transparent. We take a flat fee, pay claims super
    fast, and <a href="/giveback" aria-labelledby="how-it-works-subtitle">give back</a> what’s left to causes you
    care about.*`,
    disclaimer:
      '* Subject to board discretion and the company meeting certain financial standards. This section does not apply to Life insurance.',
    pizzaDesktop: pizza.desktop.src,
    pizzaMobile: [
      {
        image: pizza.mobile[1].src,
        text: 'A <b>flat fee</b> to </br> run everything',
      },
      {
        image: pizza.mobile[2].src,
        text: 'We pay claims <br/> <b>super fast</b>',
      },
      {
        image: pizza.mobile[3].src,
        text: `If there's money leftover,<br/> we <b>give it back to causes</b>`,
      },
    ],
    darkBackground: true,
  },
  trust: {
    bulletsLayout: 'horizontal',
    bullets: [
      {
        name: 'social',
        title: 'Designed for Social Impact',
        text: 'Lemonade Inc. is a Public Benefit Corporation and certified B-Corp. Social impact is part of our legal mission and business model - not just marketing fluff',
        image: {
          src: bCorp,
          width: 103,
          height: 88,
        },
      },
      {
        name: 'nyse',
        title: 'NYSE:LMND',
        text: 'Lemonade is a publicly traded company, listed on the New York Stock Exchange under the LMND stock symbol',
        image: {
          src: nyse,
          width: 100,
          height: 89,
        },
      },
      {
        name: 'rating',
        title: 'A-Rated and Backed by Giants',
        text: 'Lemonade is A-rated, fully regulated and reinsured by the most trusted names on the planet',
        image: {
          src: aRated,
          width: 100,
          height: 89,
        },
      },
    ],
  },
};
